import { useEffect, useId, useRef, type ChangeEvent } from 'react';
import { checkDefinition } from '@deepdive-icons';
import { Text } from '../Text/Text';
import styles from './Checkbox.module.css';

/**
 * Figma: `Checkbox` (75:756) and `_Checkbox base` (75:1029).
 *
 * The Figma component set spans four properties — Type, Checked, State and Text
 * — which is 24 variants. Here Checked and Disabled are real input state, Hover
 * is CSS, and Text is simply whether a label was passed. `state` exists only to
 * force hover for documentation.
 *
 * Radio and checkbox share one component because Figma models them as one set
 * with a `Type` property; the only differences are the corner radius and the
 * checked indicator (tick vs dot).
 *
 * The control is 14x14 as of the 2026-09 resize (it was 16x16) — see
 * Checkbox.module.css for the geometry and the per-state token table.
 */
export type CheckboxType = 'checkbox' | 'radio';
export type CheckboxState = 'default' | 'hover';

export type CheckboxProps = {
  type?: CheckboxType;
  checked?: boolean;
  /**
   * Announce the control as partially checked. **Not in Figma** — the set has
   * `Checked=False|True` and nothing between, so this deliberately leaves the
   * appearance alone and only sets the native `indeterminate` property, which
   * makes a screen reader say "mixed" instead of "not checked". Used by the
   * filter chip's "Select all" row while only some options are selected.
   */
  indeterminate?: boolean;
  disabled?: boolean;
  /** Primary label. Omit for a bare control — Figma's `Text=False` variants. */
  label?: string;
  /** Second line under the label. Requires `label`. */
  supportingText?: string;
  name?: string;
  value?: string;
  onChange?: (checked: boolean) => void;
  /**
   * Accessible name for a bare control. Not in Figma — Figma's `Text=False`
   * variants are drawn with nothing beside them, and a checkbox with neither a
   * visible label nor this is announced as unnamed. Ignored when `label` is
   * given, which already names the control.
   */
  'aria-label'?: string;
  /**
   * Draw the control but keep it out of the tab order and the accessibility
   * tree, for a row that already carries the state itself.
   *
   * **Not in Figma** — Figma has no concept of an accessibility tree. It exists
   * for `SelectMenuItem`, whose rows are `role="option"` inside a listbox: the
   * option's own `aria-selected` is what announces the choice, and a focusable
   * `<input>` inside an option is both a second announcement of the same fact
   * and a tab stop where the pattern says arrow keys move the selection. The
   * input stays in the DOM because the checked, hover and disabled skins are
   * all sibling selectors on it.
   */
  presentational?: boolean;
  /** Force the hover appearance for documentation and visual regression only. */
  state?: CheckboxState;
  id?: string;
  className?: string;
};

function CheckGlyph() {
  return (
    <svg
      className={styles.check}
      viewBox={checkDefinition.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false"
    >
      {checkDefinition.paths.map((d) => (
        <path key={d.slice(0, 24)} d={d} fill="currentColor" />
      ))}
    </svg>
  );
}

export function Checkbox({
  type = 'checkbox',
  checked = false,
  indeterminate = false,
  disabled = false,
  label,
  supportingText,
  name,
  value,
  onChange,
  'aria-label': ariaLabel,
  presentational = false,
  state,
  id,
  className,
}: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  /*
   * `indeterminate` has no HTML attribute — it is a DOM property only, so React
   * cannot set it declaratively and it has to be written on the node.
   */
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  const controlClasses = [
    styles.control,
    type === 'radio' ? styles.radio : styles.checkbox,
    state === 'hover' ? styles.forceHover : null,
  ]
    .filter(Boolean)
    .join(' ');

  const rootClasses = [
    styles.root,
    label === undefined ? styles.bare : null,
    presentational ? styles.presentational : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={rootClasses} htmlFor={inputId}>
      <span className={styles.inputWrapper}>
        <input
          ref={inputRef}
          className={styles.input}
          id={inputId}
          type={type}
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          tabIndex={presentational ? -1 : undefined}
          aria-hidden={presentational || undefined}
          aria-label={label === undefined ? ariaLabel : undefined}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange?.(event.target.checked)}
        />
        <span className={controlClasses} aria-hidden>
          {checked && (type === 'checkbox' ? <CheckGlyph /> : <span className={styles.dot} />)}
        </span>
      </span>

      {label !== undefined && (
        <span className={styles.textBlock}>
          {/* Figma keeps the label at text/primary in every state, including
              disabled — only the control changes. Not dimmed here for that
              reason, though it is worth a look for accessibility. */}
          <Text size="sm" leading="none" weight="regular" color="primary">
            {label}
          </Text>
          {supportingText !== undefined && (
            /* Figma `Supporting text` (75:930) binds `text/secondary` (#777).
               This was `tertiary` (#8d8d8d) — a shade too light. */
            <Text size="sm" leading="none" weight="regular" color="secondary">
              {supportingText}
            </Text>
          )}
        </span>
      )}
    </label>
  );
}
